import { createAsyncThunk, createAction, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { CaretakerCreateRequestDto, CaretakerResponseDto, Configuration, GlobalCaretakerApi } from "../api/client-axios";

import { ApiError } from "../model/apiError";
import { Problem } from "../model/problem";
import { initOtpState } from "./otpSlice";
import { apiConfiguration } from "./server";
import { applyRejection } from "./thunkError";

export interface CaretakerState {
    caretaker?: CaretakerResponseDto,
    status: "idle" | "loading" | "error" | "success" | "auth-error" | "saved";
    error?: string | Problem;
    code?: number;
}


const initialCaretakerState: CaretakerState = {
    status: "idle",
    error: ""
}

export const fetchCaretaker = createAsyncThunk<CaretakerResponseDto, {organizationId: string, caretakerId : string}, { rejectValue: ApiError }>('caretaker/fetchCaretaker', async (args, thunkApi) => {
    try {
        const api = new GlobalCaretakerApi(apiConfiguration());
        if (args.caretakerId === "new") {
            thunkApi.dispatch(initOtpState());
            return {} as CaretakerResponseDto;
        }
        const response = await api.apiV1AdministrationOrganizationOrganizationIdCaretakersIdGet(args.organizationId,args.caretakerId);
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});


export const saveCaretaker = createAsyncThunk<CaretakerResponseDto, {organizationId: string, caretaker: CaretakerCreateRequestDto}, { rejectValue: ApiError }>('caretaker/saveCaretaker', async (args, thunkApi) => {
    try {
        const api = new GlobalCaretakerApi(apiConfiguration());
        const response = await api.apiV1AdministrationOrganizationOrganizationIdCaretakersPost( args.organizationId, args.caretaker);
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});


export const attachCaretaker = createAsyncThunk<CaretakerResponseDto, {organizationId: string, caretakerId : string}, { rejectValue: ApiError }>('caretaker/attachCaretaker', async (args, thunkApi) => {
    try {
        const api = new GlobalCaretakerApi(apiConfiguration());
        const response = await api.apiV1AdministrationOrganizationOrganizationIdCaretakersIdAttachPost(args.caretakerId, args.organizationId);
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});

export const detachCaretaker = createAsyncThunk<CaretakerResponseDto, {organizationId: string, caretakerId : string}, { rejectValue: ApiError }>('caretaker/detachCaretaker', async (args, thunkApi) => {
    try {
        const api = new GlobalCaretakerApi(apiConfiguration());
        const response = await api.apiV1AdministrationOrganizationOrganizationIdCaretakersIdDetachPost(args.caretakerId, args.organizationId);
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});


export const updateCaretakerFields = createAction("caretaker/updateCaretakerFields",(caretaker: CaretakerCreateRequestDto) => {return { payload: caretaker }});
export const clearSaveCaretakerError = createAction('caretaker/clearError');
export const updateCaretakerPhoneNumber = createAction('caretaker/updatePhoneNumber', (phoneNumber: string) => { return { payload: phoneNumber }; });
export const initCaretakerState = createAction('caretaker/init');

const caretakerSlice = createSlice({
    name: 'caretaker',
    initialState: initialCaretakerState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchCaretaker.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(fetchCaretaker.fulfilled, (state, action) => {
                state.caretaker = action.payload;
                state.status = "success";
            })
            .addCase(fetchCaretaker.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(saveCaretaker.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(saveCaretaker.fulfilled, (state, action) => {
                state.caretaker = action.payload;
                state.status = "saved";
            })
            .addCase(saveCaretaker.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(clearSaveCaretakerError, (state, action) => {
                state.status = "success";
                state.error = "";
            })
            .addCase(attachCaretaker.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(attachCaretaker.fulfilled, (state, action) => {
                state.caretaker = action.payload;
                state.status = "saved";
            })
            .addCase(attachCaretaker.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(detachCaretaker.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(detachCaretaker.fulfilled, (state, action) => {
                state.caretaker = action.payload;
                state.status = "saved";
            })
            .addCase(detachCaretaker.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(updateCaretakerPhoneNumber, (state, action) => {
                state.caretaker!.userAccount = { mobileNumber: action.payload, emailAddress: state.caretaker?.userAccount?.emailAddress };
            })
            .addCase(initCaretakerState,(state,action)=>{
                state.status = "idle";
                state.caretaker = {};
                state.code = undefined;
                state.error = undefined;
            })
            .addCase(updateCaretakerFields,(state,action)=>{
                state.caretaker ={
                    ...state.caretaker,
                    ...action.payload as CaretakerResponseDto
                };
            });

    }
});

export default caretakerSlice.reducer;
